import React from 'react';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { Avatar, Card, Chip, Tooltip, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import {
    Calendar,
    CheckCircle,
    Clock,
    Icon as FeatherIcon,
    MapPin,
    MinusCircle,
} from 'react-feather';
import { NONE, RATING_COLORS } from 'src/constants/lead/ratings';
import { LEAD_AVATAR_FALLBACK } from 'src/modules/assistant/constants/widget';
import { getLeadDetailRoute } from 'src/modules/assistant/helpers/getLeadDetailRoute';
import { TWorkingLead } from 'src/modules/assistant/types/workingLead';
import { useWorkingLeadCardStyles } from './styles';

interface WorkingLeadCardProps {
    workingLead: TWorkingLead;
    onSelect: () => void;
}

interface IndicatorProps {
    active?: boolean;
    title: string;
    Icon: FeatherIcon;
}

const FOLLOW_UP_ICON_SIZE = 13;
const INDICATOR_ICON_SIZE = 15;
const ACTIVITY_ICON_SIZE = 12;

const Indicator = ({ active, title, Icon }: IndicatorProps) => {
    const classes = useWorkingLeadCardStyles();
    return (
        <Tooltip title={title}>
            <span
                aria-label={title}
                className={active ? classes.active : undefined}
                role="img"
            >
                <Icon size={INDICATOR_ICON_SIZE} />
            </span>
        </Tooltip>
    );
};

const FollowUp = ({ workingLead }: { workingLead: TWorkingLead }) => {
    const classes = useWorkingLeadCardStyles();
    const { t } = useTranslation();
    const scheduledAt = workingLead.nextTask?.scheduledAt
        ? moment(workingLead.nextTask.scheduledAt)
        : null;

    if (scheduledAt) {
        const overdue = scheduledAt.isBefore(moment());
        return (
            <Typography
                className={`${classes.followUp} ${
                    overdue ? classes.overdue : ''
                }`}
                variant="caption"
            >
                <Clock size={FOLLOW_UP_ICON_SIZE} />
                {overdue
                    ? t('Assistant.FollowUpOverdue', {
                          time: scheduledAt.fromNow(),
                      })
                    : t('Assistant.FollowUpScheduled', {
                          date: scheduledAt.calendar(),
                      })}
            </Typography>
        );
    }
    if (workingLead.nextRecommendedAction?.title)
        return (
            <Typography className={classes.followUp} variant="caption">
                {t('Assistant.NextRecommendedAction', {
                    action: workingLead.nextRecommendedAction.title,
                })}
            </Typography>
        );
    return (
        <Typography
            className={`${classes.followUp} ${classes.empty}`}
            variant="caption"
        >
            <MinusCircle aria-hidden="true" size={FOLLOW_UP_ICON_SIZE} />
            {t('Assistant.NoFollowUp')}
        </Typography>
    );
};

export const WorkingLeadCard = ({
    workingLead,
    onSelect,
}: WorkingLeadCardProps) => {
    const classes = useWorkingLeadCardStyles();
    const { t } = useTranslation();
    const rating = workingLead.rating || NONE;

    return (
        <Link
            className={classes.link}
            onClick={onSelect}
            to={getLeadDetailRoute(workingLead)}
        >
            <Card className={classes.card} variant="outlined">
                <div className={classes.top}>
                    <Avatar className={classes.avatar}>
                        {(workingLead.name || LEAD_AVATAR_FALLBACK)
                            .trim()
                            .charAt(0)
                            .toUpperCase()}
                    </Avatar>
                    <Typography
                        className={classes.name}
                        noWrap
                        variant="subtitle2"
                    >
                        {workingLead.name || t('Assistant.LeadWithoutName')}
                    </Typography>
                    <Chip
                        className={classes.rating}
                        label={t(`Status.${rating}`)}
                        size="small"
                        style={{
                            backgroundColor:
                                RATING_COLORS[rating] || RATING_COLORS[NONE],
                        }}
                    />
                </div>
                <Typography
                    color="textSecondary"
                    display="block"
                    noWrap
                    variant="caption"
                >
                    {workingLead.status || t('Assistant.StatusUnavailable')}
                </Typography>
                <FollowUp workingLead={workingLead} />
                <div className={classes.footer}>
                    <Indicator
                        active={workingLead.hasAppointment}
                        Icon={Calendar}
                        title={t('Assistant.HasAppointment')}
                    />
                    <Indicator
                        active={workingLead.hasVisit}
                        Icon={MapPin}
                        title={t('Assistant.HasVisit')}
                    />
                    <Indicator
                        active={workingLead.hasSale}
                        Icon={CheckCircle}
                        title={t('Assistant.HasSale')}
                    />
                    <Typography
                        className={classes.activity}
                        color="textSecondary"
                        variant="caption"
                    >
                        <Clock size={ACTIVITY_ICON_SIZE} />
                        {moment(workingLead.updatedAt).fromNow()}
                    </Typography>
                </div>
            </Card>
        </Link>
    );
};
